import { CheckCircleTwoTone, DownOutlined } from '@ant-design5/icons';
import { ItemType } from 'antd5/es/menu/interface';
import { StyledTitle } from 'components/DataTable/Views/Views.style';
import { Button, Dropdown } from 'components/ui';
import { useContext } from 'react';
import { useTheme } from 'styled-components';
import { DataTableContext } from 'components/DataTable';
import {
  AtlasGqlTableView,
  useGetBladeCardsQuery,
  useGetMakeModelOptionsQuery,
  useSiteBladeMakeModelsQuery,
  useTurbineBladeMakeModelsQuery,
} from 'types/atlas-graphql';
import { useLocation } from 'react-router-dom';
import { uuidRegex as uuidRegexString } from 'utils/constants';

const uuidRegex = new RegExp(uuidRegexString);

type BladeCardAssetFilter = {
  key: string;
  value: [string];
};

type BladeCardsProps = {
  tableId?: string;
  applyViewToTable: (tableViewToApply?: AtlasGqlTableView) => void;
  onSelect?: () => void;
};

export const BladeCards: React.FC<BladeCardsProps> = ({ tableId, applyViewToTable, onSelect }) => {
  const theme = useTheme();
  const { tableView } = useContext(DataTableContext);
  const location = useLocation();

  const { data: hotZonesData, loading } = useGetBladeCardsQuery();
  const { data: makeModelData } = useGetMakeModelOptionsQuery({
    variables: {
      makeInput: {
        fieldName: 'blade_make',
      },
      modelInput: {
        fieldName: 'blade_model',
      },
      independentFieldNameForCombinations: 'blade_make',
    },
  });

  const isSiteDetailsPage = tableId === 'site-damage-list-table2';
  const isPotentiallyTurbineDetailsPage = tableId === 'assets-damage-list-table2';
  const assetId = location.pathname.split('/')[2];

  const { data: siteBladeMakeModelsData } = useSiteBladeMakeModelsQuery({
    variables: {
      siteId: assetId,
    },
    skip: !isSiteDetailsPage || !uuidRegex.test(assetId),
  });
  const { data: turbineBladeMakeModelsData } = useTurbineBladeMakeModelsQuery({
    variables: {
      turbineId: assetId,
    },
    skip: !isPotentiallyTurbineDetailsPage || !uuidRegex.test(assetId),
  });

  const items: ItemType[] = (hotZonesData?.bladeHotZoneTableViews ?? [])
    .filter(view => {
      const viewMake = view.filters.asset?.find(
        (filter: BladeCardAssetFilter) => filter.key === 'componentMake'
      )?.value?.[0];
      const viewModel = view.filters.asset?.find(
        (filter: BladeCardAssetFilter) => filter.key === 'componentModel'
      )?.value?.[0];

      if (siteBladeMakeModelsData?.siteBladeMakeModels?.length) {
        return (
          viewMake &&
          viewModel &&
          siteBladeMakeModelsData.siteBladeMakeModels.some(
            ({ make, model }) => make === viewMake && model === viewModel
          )
        );
      }

      if (turbineBladeMakeModelsData?.turbineBladeMakeModels?.length) {
        return (
          viewMake &&
          viewModel &&
          turbineBladeMakeModelsData.turbineBladeMakeModels.some(
            ({ make, model }) => make === viewMake && model === viewModel
          )
        );
      }

      return (
        viewMake &&
        makeModelData?.configuredMakes.existingValues?.includes(viewMake) &&
        viewModel &&
        makeModelData?.configuredModels.existingValues?.includes(viewModel)
      );
    })
    .map(view => ({
      key: view.id,
      label: (
        <StyledTitle>
          {view.id === tableView?.id && <CheckCircleTwoTone twoToneColor={theme.green} />}
          <span>{view.name}</span>
        </StyledTitle>
      ),
      onClick: () => {
        if (tableId) {
          applyViewToTable({ ...view, tableName: tableId });
        }
        if (onSelect) {
          onSelect();
        }
      },
    }));

  return (
    <Dropdown
      menu={{ items, style: { maxHeight: 350, overflowY: 'scroll' } }}
      trigger={['click']}
      placement="bottomLeft"
    >
      <Button data-testid="blade-cards-dropdown" loading={loading}>
        Blade Cards <DownOutlined />
      </Button>
    </Dropdown>
  );
};
